import { defineRecipe, type RecipeVariantProps } from '@chakra-ui/react';

export const iconButtonRecipe = defineRecipe({
  className: 'icon-button',
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'l',
    cursor: 'pointer',
    transitionProperty: 'background-color, border-color, color, box-shadow, opacity',
    transitionDuration: '0.2s',
    flexShrink: 0,
    gap: 0,
    _disabled: {
      cursor: 'not-allowed',
      opacity: 'disabled',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'text.info',
      outlineOffset: '-2px',
    },
    _icon: {
      flexShrink: 0,
      boxSize: 'icon.s',
      fontSize: 'inherit',
    },
    '&[data-loading]': {
      color: 'text.quaternary',
    },
  },
  variants: {
    variant: {
      primary: {
        bg: 'transparent',
        color: 'text.primary',
        border: 'none',
        borderRadius: 'm',
        _hover: {
          bg: 'background.canvas',
          color: 'text.primary',
        },
        _active: {
          bg: 'background.canvas',
          color: 'text.primary',
        },
        _disabled: {
          color: 'text.primary',
        },
      },
      secondary: {
        bg: 'background.primary',
        color: 'text.primary',
        borderColor: 'border.accent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: 'l',
        _hover: {
          bg: 'background.primary',
          borderColor: 'border.accent.active',
          color: 'text.primary',
        },
        _active: {
          bg: 'background.subtle.green',
          borderColor: 'border.accent.active',
          color: 'text.primary',
        },
        _disabled: {
          bg: 'background.primary',
          borderColor: 'border.accent',
          color: 'text.primary',
        },
      },
      tertiaryCircular: {
        bg: 'background.primary',
        color: 'text.primary',
        borderColor: 'border.accent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: 'full',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        _hover: {
          bg: 'background.canvas',
          borderColor: 'border.accent.active',
          color: 'text.primary',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        },
        _active: {
          bg: 'background.canvas',
          borderColor: 'border.accent.active',
          color: 'text.primary',
          boxShadow: 'none',
        },
        _disabled: {
          bg: 'background.primary',
          borderColor: 'border.accent',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    size: {
      s: {
        minW: '24px',
        minH: '24px',
        p: '2xs',
        _icon: {
          boxSize: 'icon.s',
        },
      },
      m: {
        p: 'xs',
        _icon: {
          boxSize: 'icon.m',
        },
      },
      l: {
        p: 'xs',
        _icon: {
          boxSize: 'icon.l',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'm',
  },
});

export type IconButtonRecipeProps = RecipeVariantProps<typeof iconButtonRecipe>;
